import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import CancelModal from 'components/manageSessionModal/cancelModal';
import { toUkDisplayDate } from 'helpers/ukDate';

const OPTIONS = [
    { value: false, label: 'All patients' },
    { value: true, label: 'Own patients only' },
];

/**
 * The global switch for "own patients only" default lists. superAdmin only. Manual, no expiry:
 * switching it off is also the override for cover situations.
 */
export default function PatientListRestrictionCard() {
    const [setting, setSetting] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [confirmOn, setConfirmOn] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await AppSettingsService.getSettings();
            setSetting(data.patientListRestriction);
            setError('');
        } catch (e) {
            setError('Could not load this setting.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (enabled) => {
        setIsSaving(true);
        try {
            const { data } = await AppSettingsService.setPatientListRestriction(enabled);
            setSetting(data.patientListRestriction);
            setError('');
        } catch (e) {
            setError('Could not change the setting.');
        } finally {
            setIsSaving(false);
            setConfirmOn(false);
        }
    };

    const choose = (enabled) => {
        if (!setting || enabled === setting.enabled || isSaving) {
            return;
        }
        // Switching on narrows every clinician's lists at once - including staff who have not been
        // granted full access yet. Ask.
        if (enabled) {
            setConfirmOn(true);
            return;
        }
        save(false);
    };

    return (
        <Card className="card-auth overflow-hidden" data-testid="patient-list-restriction-card">
            <CardBody>
                <label className="details-caption p-2">Patient lists</label>
                <div className="p-2">
                    {error ? <div className="text-danger mb-2" role="alert">{error}</div> : null}

                    {setting ? (
                        <>
                            <div className="mh-options" role="radiogroup" aria-label="Patient lists">
                                {OPTIONS.map(option => (
                                    <label
                                        key={String(option.value)}
                                        className={`mh-option${setting.enabled === option.value ? ' mh-option--selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="patientListRestriction"
                                            checked={setting.enabled === option.value}
                                            disabled={isSaving}
                                            onChange={() => choose(option.value)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>

                            <p className="text-muted font-size-12 mt-2 mb-1" data-testid="patient-list-description">
                                {setting.enabled
                                    ? 'Clinicians\' Patients and Appointments pages show only their own patients, '
                                        + 'unless you have granted them full access on the Clinicians page. '
                                        + 'Search still finds any patient.'
                                    : 'Everyone sees every doctor\'s patients and sessions. Switch to "All patients" '
                                        + 'any time for cover.'}
                            </p>

                            {setting.updatedAt ? (
                                <p className="text-muted font-size-12 mb-0" data-testid="patient-list-changed">
                                    {`Last changed${setting.updatedBy ? ` by ${setting.updatedBy.name}` : ''} on ${toUkDisplayDate(setting.updatedAt)}.`}
                                </p>
                            ) : null}
                        </>
                    ) : (!error && <div className="text-muted">Loading…</div>)}
                </div>
            </CardBody>

            <CancelModal
                visibleModal={confirmOn}
                setVisibleModal={setConfirmOn}
                handleClose={() => save(true)}
                header="Show clinicians only their own patients?"
                text="Every clinician's Patients and Appointments pages will show only their own patients straight away. Staff who work across all doctors' lists - admin and ID checks - need full access granted on the Clinicians page first."
                cancelButtonTitle="Cancel"
            />
        </Card>
    );
}
